import React, {memo} from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'

/* Constants */
import {DAVINCI_EVENT_NAMES} from '../../../../modules/analytics/davinci/davinciConstants'

/* Components */
import Rating from './Rating'

/* Utils */
import {yieldToMain} from '../../../../utils/performance'
import {generateExperiments} from '../../../../modules/analytics/analyticsUtils'

/* Styles */
import s from './product-card.scss'

const ProductInfo = (props) => {
  const {
    title,
    name,
    sku,
    productId,
    listingId,
    listings,
    brandName,
    price,
    merchantId,
    merchantName,
    originalPrice,
    discountRate,
    customerReviewCount,
    customerReviewScore,
    categorizedLabels,
    index,
    placementId,
    isAdProduct,
    adInfo
  } = props
  const valueAddedService = categorizedLabels?.valueAddedService?.[0]
  const campaign = categorizedLabels?.campaign?.[0]
  const [cardState, setCardState] = React.useState('default')
  const [isPending, setIsPending] = React.useState(false)
  const cardStates = {
    default: {
      text: 'Sepete Ekle',
      className: s.addToCart,
    },
    Added: {
      text: 'Sepete Eklendi',
      className: cx(s.addToCart, s.addToCartAdded),
    },
    Loading: {
      text: null,
      className: cx(s.addToCart, s.addToCartLoading),
    },
    Error: {
      text: 'Sepete Eklenemedi',
      className: cx(s.addToCart, s.addToCartError),
    },
  }

  const handleAddToCart = async(e) => {
    e.preventDefault()
    handleAddToCartClickEvent()
    const addToCartData = {
      listingId: listingId || '',
      sku: sku || '',
      name: name || '',
      quantity: 1,
    }
    await yieldToMain()
    if (process.env.BROWSER && window?.Checkout?.AddToCart) {
      setCardState('Loading')
      setIsPending(true)
      window.Checkout.AddToCart(addToCartData, 'HomePage', {
        callback: () => {
          setCardState('Added')
          setIsPending(false)

          setTimeout(() => {
            setCardState('default')
          }, 2000)
        },
        errorHandler: (e) => {
          setCardState('Error')
          setIsPending(false)
          setTimeout(() => {
            setCardState('default')
          }, 2000)
          console.error('Checkout HomePage Campaign Add To Cart Error', e)
        },
      })
    }
  }

  const handleAddToCartClickEvent = () => {
    let data = {
      canonical_url: window?.utagData?.canonical_url || '',
      category: {},
      category_hierarchy: '',
      category_status: '',
      experiments: generateExperiments(),
      listings: listings || [],
      location: 'recommendation',
      merchant: merchantName,
      merchantId: merchantId,
      page_type: window?.hepsiBus?.parsers?.getPageType(),
      page_value: window?.hepsiBus?.parsers?.getPageValue(),
      placement_id: placementId,
      placement_title: title,
      product: [{
        product_id: productId || '',
        labels: ['incentiveToBuy', 'valueAddedService', 'campaign'].map(labelName => {
          const labels = categorizedLabels?.[labelName]
          const firstLabel = labels?.[0]

          return {
            type: labelName,
            id: firstLabel?.tagName || null,
            displayName: firstLabel?.imageUrl || firstLabel?.text || null
          }
        }),
        listingId: listingId || '',
        mainProduct: true,
        merchantId: merchantId,
        name: name,
        product_status: '',
        position: index + 1,
        price: price?.value?.toString(),
        sku: sku || '',
      }],
      customerReviewCount: customerReviewCount || '',
      customerReviewScore: customerReviewScore || ''
    }

    window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.ADD_TO_CART_CLICK, data, window?.hepsiBus?.parsers?.getGlobals())
  }

  return (
    <div className={s.productInfo} aria-hidden='true'>
      {
        valueAddedService?.text && (
          <div
            className={s.freeShipping}
            style={{
              backgroundColor: `#${valueAddedService.color}`,
              color: `#${valueAddedService.textColor}`,
            }}>
            {valueAddedService.text}
          </div>
        )
      }
      <div className={s.productTitle}>
        <span className={s.productBrand}>{brandName}</span>
        {name}
      </div>
      <div className={s.productRating} aria-hidden='true'>
        <Rating rate={customerReviewScore} />
        <span className={s.ratingCount}>{customerReviewCount}</span>
      </div>
      {
        discountRate > 0 && originalPrice?.value && (
          <div className={s.productPrice} aria-hidden='true'>
            <span className={s.oldPrice}>{`${originalPrice?.displayValue} ${originalPrice?.currency}`}</span>
            <span className={s.discount}> {`%${discountRate}`} </span>
          </div>
        )
      }
      <div className={s.productPrice}>
        <span className={discountRate > 0 ? s.newPrice : s.newPriceDefault}>
          {`${price?.displayValue} ${price?.currency}`}
        </span>
      </div>
      {
        campaign?.text && (
          <div className={discountRate > 0 ? s.creditInfo : s.creditInfoDefault} style={{color: campaign?.color}}>{campaign.text}</div>
        )
      }
      <div
        className={cardState === 'default' ? s.addToCart : cardStates[cardState].className}
        role='button'
        aria-live='assertive'
        tabIndex='0'
        aria-label='Sepete Ekle'
        onClick={handleAddToCart}>
        {!isPending && cardStates[cardState].text}
        {isPending && <div className={s.isPendingLoader}/>}
      </div>
    </div>
  )
}

ProductInfo.propTypes = {
  name: PropTypes.string.isRequired,
  brandName: PropTypes.string.isRequired,
  price: PropTypes.shape({
    value: PropTypes.number,
    currency: PropTypes.string,
    displayValue: PropTypes.string,
  }).isRequired,
  originalPrice: PropTypes.shape({
    value: PropTypes.number,
    currency: PropTypes.string,
    displayValue: PropTypes.string,
  }),
  discountRate: PropTypes.number,
  customerReviewCount: PropTypes.number,
  categorizedLabels: PropTypes.shape({
    valueAddedService: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
        color: PropTypes.string,
        textColor: PropTypes.string,
      }),
    ),
    campaign: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
      }),
    ),
  }),
}

export default memo(ProductInfo)
